import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
} from "@mui/material";
import { CustomModal } from "~components/atoms";
import { authFetch } from "~helpers/authFetch";
import { useToastNotificationContext } from "~contexts/ToastNotification";

interface CreateBrandModalProps {
  isOpened: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback quando a marca é criada com sucesso
}

const CreateBrandModal: React.FC<CreateBrandModalProps> = ({
  isOpened,
  onClose,
  onSuccess,
}) => {
  const theme: any = useTheme();
  const { setToastData } = useToastNotificationContext();
  const [brandFormData, setBrandFormData] = useState({
    name: "",
    string: "",
    provider: "VTEX",
    provider_url: "",
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleClose = () => {
    setBrandFormData({
      name: "",
      string: "",
      provider: "VTEX",
      provider_url: "",
    });
    onClose();
  };

  const handleCreateBrand = async () => {
    try {
      if (!brandFormData.name.trim()) {
        setToastData(
          {
            title: "Erro",
            text: "Nome da marca é obrigatório",
          },
          "error"
        );
        return;
      }

      if (!brandFormData.string.trim()) {
        setToastData(
          {
            title: "Erro",
            text: "Stream name é obrigatório",
          },
          "error"
        );
        return;
      }

      if (!brandFormData.provider.trim()) {
        setToastData(
          {
            title: "Erro",
            text: "Provider é obrigatório",
          },
          "error"
        );
        return;
      }

      if (!brandFormData.provider_url.trim()) {
        setToastData(
          {
            title: "Erro",
            text: "Provider URL é obrigatório",
          },
          "error"
        );
        return;
      }

      setIsLoading(true);

      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users/brands`,
        method: "POST",
        data: {
          name: brandFormData.name.trim(),
          string: brandFormData.string.trim(),
          provider: brandFormData.provider.trim(),
          provider_url: brandFormData.provider_url.trim(),
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.message || "Erro ao criar marca");
        (error as any).status = response.status;
        throw error;
      }

      await response.json();

      setToastData(
        {
          title: "Sucesso",
          text: "Marca criada com sucesso",
        },
        "success"
      );

      handleClose();

      // Chamar callback de sucesso se fornecido
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error("Error creating brand:", error);
      let errorMessage = "Erro ao criar marca";

      if (error.status === 400) {
        errorMessage = error.message || "Dados inválidos ou stream name já existe";
      }

      setToastData(
        {
          title: "Erro",
          text: errorMessage,
        },
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CustomModal isOpened={isOpened} onClose={handleClose}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{ color: theme.palette.grey[0], fontSize: "23px" }}
        >
          Nova marca
        </Typography>

        <Box
          component="form"
          autoComplete="off"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <TextField
            label="nome da marca"
            placeholder="nome da marca"
            value={brandFormData.name}
            onChange={(e) =>
              setBrandFormData((prev) => ({ ...prev, name: e.target.value }))
            }
            fullWidth
            required
            autoComplete="off"
            disabled={isLoading}
          />

          <TextField
            label="string"
            placeholder="string"
            value={brandFormData.string}
            onChange={(e) =>
              setBrandFormData((prev) => ({ ...prev, string: e.target.value }))
            }
            fullWidth
            required
            autoComplete="off"
            disabled={isLoading}
          />

          <FormControl fullWidth required disabled={isLoading}>
            <InputLabel>Provider</InputLabel>
            <Select
              value={brandFormData.provider}
              onChange={(e) =>
                setBrandFormData((prev) => ({ ...prev, provider: e.target.value }))
              }
              label="Provider"
            >
              <MenuItem value="VTEX">VTEX</MenuItem>
              <MenuItem value="SHOPIFY">SHOPIFY</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Provider URL"
            placeholder="https://loja.vtexcommercestable.com.br"
            value={brandFormData.provider_url}
            onChange={(e) =>
              setBrandFormData((prev) => ({ ...prev, provider_url: e.target.value }))
            }
            fullWidth
            required
            autoComplete="off"
            disabled={isLoading}
          />
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "flex-start",
            mt: 2,
          }}
        >
          <Button
            variant="contained"
            onClick={handleCreateBrand}
            disabled={isLoading}
            sx={{
              height: "48px",
            }}
          >
            {isLoading ? "Criando..." : "adicionar marca"}
          </Button>
          <Button
            variant="text"
            onClick={handleClose}
            disabled={isLoading}
            sx={{
              color: "#FFFFFF",
              backgroundColor: "transparent",
              "&:hover": {
                backgroundColor: "transparent",
              },
            }}
          >
            cancelar
          </Button>
        </Box>
      </Box>
    </CustomModal>
  );
};

export default CreateBrandModal;

